import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CarouselContext, Slider, Slide } from 'pure-react-carousel';
import { kebabCase } from 'lodash';

import UserContent from '~/components/UserContent';

import { h2 } from '~/styles/typography';
import { transition } from '~/styles/transition';

import { formatText } from '~/utilities/helpers';

const H4 = styled.h4`
    ${h2};
    color: ${({ theme }) => theme.colors.primary.black};
    margin-bottom: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        margin-bottom: 35px;
    }
`;

const SlideContent = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
`;

const SlideText = styled.div`
    border-radius: 61px;
    padding: 45px 45px 85px 45px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        padding: 65px 15% 20px 45px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        padding: 95px 30% 30px 75px;
    }
`;

const SlideDescription = styled(UserContent)`
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: 1.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    margin-bottom: 60px;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        font-size: 1.8rem;
        margin-bottom: 85px;
    }
`;

const StyledSlider = styled(Slider)`
    padding-top: 42px;

    .carousel__slide {
        ${SlideText} {
            opacity: 1;
            ${transition({ speed: 1250, delay: 500 }, 'opacity')};
        }
    }

    .carousel__slide--hidden {
        ${SlideText} {
            opacity: 0;
        }
    }
`;

const WashInstructionsTextSlider = ({
    steps,
    mainSliderState,
    mainSliderDispatch
}) => {
    const carouselContext = useContext(CarouselContext);

    useEffect(() => {
        function onChange() {
            mainSliderDispatch({
                type: 'CHANGE_SLIDE',
                slideIndex: carouselContext.state.currentSlide
            });
        }

        carouselContext.subscribe(onChange);
        return () => carouselContext.unsubscribe(onChange);
    }, [carouselContext, mainSliderDispatch]);

    useEffect(() => {
        carouselContext.setStoreState({
            currentSlide: mainSliderState.currentSlide
        });
    }, [mainSliderState.currentSlide, carouselContext]);

    return (
        <StyledSlider classNameAnimation="fade-animation">
            {steps.map(({ title, description }, i) => (
                <Slide key={kebabCase(title)} index={i}>
                    <SlideContent>
                        <SlideText>
                            <H4>{formatText(title)}</H4>
                            <SlideDescription content={description} />
                        </SlideText>
                    </SlideContent>
                </Slide>
            ))}
        </StyledSlider>
    );
};

WashInstructionsTextSlider.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            description: PropTypes.string
        })
    ).isRequired,
    mainSliderState: PropTypes.object,
    mainSliderDispatch: PropTypes.func
};

export default WashInstructionsTextSlider;
